import { useEffect, useMemo, useState } from 'react';
import { Link as LinkIcon } from 'lucide-react';
import {
  ChartEmpty,
  ScoreBar,
  TrendChart,
  ZONE_LABEL,
  zoneOf,
  type Thresholds,
} from '@/components/ui/charts';
import { CitationChip } from '@/components/ui/citation-chip';
import { PostureChip } from '@/components/ui/posture-chip';
import { StatusChip } from '@/components/ui/status-chip';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import { WorkspaceButton } from '@/components/ui/workspace/button';
import { ScoreCriterionModal } from '@/components/modals/score-criterion-modal';
import { LinkXrefModal } from '@/components/modals/link-xref-modal';
import {
  useCanAdjustAssessment,
  useInvalidateHealthReadiness,
  type HrCriterion,
  type HrDomain,
} from '@/lib/api/health-readiness';
import { useReadinessRubricQuery, type RubricCriterion } from '@/lib/api/readiness-rubric';
import {
  useCreateReadinessScoreMutation,
  useOverrideReadinessScoreMutation,
  useReadinessScoresQuery,
} from '@/lib/api/readiness-scorecard';
import {
  useCrossModuleReferencesQuery,
  useDetachXrefMutation,
  type CrossModuleReferenceItem,
} from '@/lib/api/readiness-xref';
import { formatDayMonth } from '@/lib/utils/format';
import {
  criterionTrendPoints,
  evidenceKindLabel,
  evidenceState,
  firstScoredSnapshot,
  isNewerThan,
  keptLinks,
  linkKey,
  suggestionTitle,
  whoWhen,
} from './detail-model';
import { AI_TEXT, AiBox, DetailCard, Meta, RecheckButton, useRecheck } from './detail-parts';
import { formatPercent, formatWeight } from './model';
import { useHrNavigate } from './navigation';
import { formatChange, formatPoints, gateDistance } from './overview/overview-model';

/**
 * Go-live criterion detail (T-0243 / E22-F02; prototype `criterionDetail()`),
 * top to bottom:
 *
 *   - the name, its category, its weight and who scored it; Re-check (leads);
 *   - the score against the two gates (`ScoreBar`, the AI's reading marked
 *     when it differs), the points it contributes and its distance to each
 *     gate (one value's difference, never a sum), and the score large;
 *   - the AI reading (E21 T-0238, the criterion judge): its reasoning, the
 *     passages it cites and the records it suggests as evidence, each kept
 *     unless the reviewer unticks it before Confirm. Confirm creates the
 *     score (or, on a scored criterion, records the AI's as an override)
 *     with the kept links; Score differently opens the scoring dialog and
 *     settles the proposal there;
 *   - the evidence the score reads, live, with Link evidence and Unlink
 *     (leads), the criterion's description, and the score over time.
 *
 * Every number is the API's: the score, its contribution, the trend and
 * its change. Nothing here adds anything up.
 */
export interface CriterionPaneProps {
  engagementId: string;
  criterion: HrCriterion;
  thresholds: Thresholds;
  domains: HrDomain[];
  /** A deviation or deliverable in the evidence opens Contract Compliance. */
  onOpenContract?: (kind: 'deviations' | 'deliverables') => void;
}

const DEFAULT_RUBRIC_TEXT =
  'No description yet. Say what 100%, 50% and 0% look like in Manage criteria; the AI scores against it.';

/** The rubric row shape the dialogs expect, from the composite. */
function asRubricCriterion(c: HrCriterion): RubricCriterion {
  return {
    id: c.id,
    category: c.category,
    name: c.name,
    weight: c.weight,
    score_type: c.scoreType,
    pass_threshold: null,
    cross_module_sources: [],
    display_order: c.displayOrder,
    provenance: 'consultant_authored',
    is_active: true,
    override_record_id: null,
    created_at: '',
    updated_at: '',
  };
}

const newKey = () =>
  typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `score-confirm-${Date.now()}`;

export function CriterionPane({
  engagementId,
  criterion,
  thresholds,
  domains,
  onOpenContract,
}: CriterionPaneProps) {
  const go = useHrNavigate(engagementId);
  const canLead = useCanAdjustAssessment();
  const recheck = useRecheck(engagementId, 'criterion');
  const [scoring, setScoring] = useState(false);
  const [linking, setLinking] = useState(false);
  const rubric = useReadinessRubricQuery(engagementId);
  const scores = useReadinessScoresQuery(engagementId);
  const refs = useCrossModuleReferencesQuery(engagementId, { criterion_id: criterion.id });
  const invalidate = useInvalidateHealthReadiness(engagementId);
  const confirm = useCreateReadinessScoreMutation(engagementId);
  const confirmOverScore = useOverrideReadinessScoreMutation(engagementId);
  const detach = useDetachXrefMutation(engagementId);
  // Suggested links the reviewer unticked; every other one is kept.
  const [dropped, setDropped] = useState<ReadonlySet<string>>(new Set());
  const candidateId = criterion.aiCandidate?.proposalId ?? null;
  useEffect(() => setDropped(new Set()), [candidateId]);

  const rubricCriterion = useMemo(
    () => rubric.data?.criteria.find((c) => c.id === criterion.id) ?? asRubricCriterion(criterion),
    [rubric.data, criterion],
  );
  const scoreItem = scores.data?.items.find((s) => s.criterion_id === criterion.id) ?? null;
  const references = useMemo(
    () => (refs.data?.items ?? []).filter((r) => r.criterion_id === criterion.id),
    [refs.data, criterion.id],
  );
  const today = useMemo(() => new Date(), []);

  const scored = criterion.scorePercent !== null;
  const zone = zoneOf(criterion.scorePercent, thresholds);
  const candidate = criterion.aiCandidate;
  const candidateLabel = candidate
    ? candidate.scorePercent !== null
      ? formatPercent(candidate.scorePercent)
      : String(candidate.score)
    : null;
  const confirmedMatches =
    candidate !== null &&
    scored &&
    ((candidate.scorePercent !== null && candidate.scorePercent === criterion.scorePercent) ||
      String(candidate.score) === String(criterion.score));
  const suggested = candidate?.suggestedLinks ?? [];
  const points = formatPoints(criterion.weight * 100);
  const scoredLine = whoWhen(criterion.scoredAt, criterion.scoredBy, 'Scored');

  const confirmCandidate = () => {
    if (!candidate) return;
    const kept = keptLinks(suggested, dropped);
    if (scored && criterion.scoreId) {
      // A saved score changes only by an override; the API records it with
      // the AI's reasoning, so the prior score stays in the history.
      confirmOverScore.mutate(
        {
          scoreId: criterion.scoreId,
          input: {
            ai_proposal_id: candidate.proposalId,
            committed_confidence: candidate.confidence,
            ...kept,
          },
          idempotencyKey: newKey(),
        },
        { onSuccess: () => invalidate() },
      );
      return;
    }
    confirm.mutate(
      {
        body: {
          criterion_id: criterion.id,
          score: candidate.score,
          origin: 'ai_extracted_confirmed_as_is',
          committed_confidence: candidate.confidence,
          ai_proposal_id: candidate.proposalId,
          ...kept,
        },
        idempotencyKey: newKey(),
      },
      { onSuccess: () => invalidate() },
    );
  };
  const confirming = confirm.isPending || confirmOverScore.isPending;
  const confirmError = confirm.error ?? confirmOverScore.error;

  const openEvidence = (ref: CrossModuleReferenceItem) => {
    if (ref.source_entity_kind === 'domain_rating') {
      const domain = domains.find(
        (d) => d.id === ref.source_entity_id || d.latestAssessmentId === ref.source_entity_id,
      );
      if (domain) go({ tab: 'domains', id: domain.id });
      return;
    }
    if (ref.source_entity_kind === 'finding') {
      go({ tab: 'findings' });
      return;
    }
    if (ref.source_entity_kind === 'deviation') onOpenContract?.('deviations');
    if (ref.source_entity_kind === 'deliverable') onOpenContract?.('deliverables');
  };

  const trend = criterionTrendPoints(criterion, thresholds, today);
  const since = firstScoredSnapshot(criterion);

  return (
    <div className="flex flex-col gap-4" data-testid="criterion-pane">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="t-h1 flex flex-wrap items-center gap-2.5">
            {criterion.name}
            <StatusChip tone="neutral">{criterion.category}</StatusChip>
          </h1>
          <div className="text-meta text-ink-500">
            Weight {formatWeight(criterion.weight)} of the go-live total ·{' '}
            {scoredLine.charAt(0).toLowerCase() + scoredLine.slice(1)}
          </div>
        </div>
        {canLead ? (
          <RecheckButton
            what={criterion.name}
            pending={recheck.pending}
            onClick={() => recheck.request(criterion.id)}
          />
        ) : null}
      </div>
      {recheck.askedFor === criterion.id ? (
        <p role="status" className="text-meta text-ink-500">
          Re-check asked for: the AI reads every piece of evidence for {criterion.name} again. Its
          reading shows here when it is done.
        </p>
      ) : null}
      {recheck.error ? <ErrorMessage error={recheck.error} /> : null}

      <section
        aria-label="Score"
        className="grid items-center gap-6 rounded-card border border-ink-100 bg-paper-0 p-4 md:grid-cols-[1fr_180px]"
      >
        <div className="min-w-0">
          <ScoreBar
            variant="full"
            value={criterion.scorePercent}
            aiValue={candidate?.scorePercent ?? null}
            thresholds={thresholds}
            label={criterion.name}
          />
          <ScoreFacts criterion={criterion} thresholds={thresholds} points={points} />
        </div>
        <div className="flex flex-col items-start gap-1.5 md:items-end">
          <span
            className={
              scored
                ? 'text-[44px] font-semibold leading-[48px] tracking-[-0.02em] text-ink-900'
                : 'text-[44px] font-semibold leading-[48px] text-ink-300'
            }
          >
            {formatPercent(criterion.scorePercent)}
          </span>
          <PostureChip status={zone} dot aria-label={`Score: ${ZONE_LABEL[zone]}`}>
            {ZONE_LABEL[zone]}
          </PostureChip>
        </div>
      </section>

      {candidate ? (
        <AiBox
          confidence={candidate.confidence}
          isNew={isNewerThan(candidate.createdAt, criterion.scoredAt)}
        >
          <p className={AI_TEXT}>
            Proposes <strong>{candidateLabel}</strong>.{' '}
            {candidate.reasoning ?? 'From the evidence below.'}{' '}
            {scored
              ? confirmedMatches
                ? 'You confirmed it.'
                : `Your confirmed score is ${formatPercent(criterion.scorePercent)}.`
              : ''}
          </p>
          {candidate.citations.length > 0 ? (
            <div className="flex flex-wrap gap-1.5" aria-label="Cited passages">
              {candidate.citations.map((c) => (
                <CitationChip key={c.id} cite={{ ...c, confidence: candidate.confidence }} />
              ))}
            </div>
          ) : null}
          {suggested.length > 0 && !confirmedMatches ? (
            <fieldset className="flex flex-col rounded-card border border-ai-surface-border bg-paper-0 px-3 py-1.5">
              <legend className="px-1 text-meta font-medium text-ai-ink">
                Link as evidence when you confirm
              </legend>
              {suggested.map((l) => {
                const key = linkKey(l);
                return (
                  <label
                    key={key}
                    className="grid cursor-pointer grid-cols-[16px_1fr] items-center gap-2 border-t border-ink-100 py-1.5 text-body-sm text-ink-900 first-of-type:border-t-0"
                  >
                    <input
                      type="checkbox"
                      checked={!dropped.has(key)}
                      onChange={(e) => {
                        const next = new Set(dropped);
                        if (e.target.checked) next.delete(key);
                        else next.add(key);
                        setDropped(next);
                      }}
                      className="h-3.5 w-3.5 accent-accent-amber-600"
                    />
                    <span className="truncate">{suggestionTitle(l, domains)}</span>
                  </label>
                );
              })}
            </fieldset>
          ) : null}
          {confirmError ? <ErrorMessage error={confirmError} /> : null}
          <div className="flex flex-wrap items-center gap-2">
            {!confirmedMatches ? (
              <WorkspaceButton variant="primary" onClick={confirmCandidate} disabled={confirming}>
                {confirming ? 'Confirming…' : `Confirm ${candidateLabel}`}
              </WorkspaceButton>
            ) : null}
            <WorkspaceButton variant="secondary" onClick={() => setScoring(true)}>
              Score differently…
            </WorkspaceButton>
            <span className="text-meta text-ink-500">
              {scored ? scoredLine : 'The score is yours; the reading cites the documents it used.'}
            </span>
          </div>
        </AiBox>
      ) : scored ? (
        <section className="flex flex-wrap items-center justify-between gap-3 rounded-card border border-ink-100 bg-paper-0 px-4 py-3">
          <p className="text-body-sm text-ink-700">{scoredLine}. No AI reading waiting.</p>
          <WorkspaceButton variant="secondary" onClick={() => setScoring(true)}>
            Score differently…
          </WorkspaceButton>
        </section>
      ) : (
        <AiBox title="No AI reading yet">
          <p className={AI_TEXT}>
            Score it yourself; the AI proposes a score when a document speaks to this criterion.
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <WorkspaceButton variant="primary" onClick={() => setScoring(true)}>
              Score now
            </WorkspaceButton>
          </div>
        </AiBox>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <DetailCard
          title="Evidence this score reads"
          label="Evidence"
          aside={
            canLead ? (
              <WorkspaceButton variant="secondary" onClick={() => setLinking(true)}>
                <LinkIcon className="h-3.5 w-3.5" aria-hidden="true" />
                Link evidence
              </WorkspaceButton>
            ) : (
              <Meta>live</Meta>
            )
          }
        >
          {refs.isLoading ? (
            <div className="flex items-center gap-2 text-meta text-ink-500">
              <Spinner /> Loading evidence…
            </div>
          ) : refs.error ? (
            <ErrorMessage error={refs.error} />
          ) : references.length === 0 ? (
            <p className="text-meta text-ink-500">
              No evidence linked yet.
              {canLead
                ? ' Link a deliverable, deviation, finding or domain rating this score should read.'
                : ''}
            </p>
          ) : (
            <ul className="flex flex-col">
              {references.map((ref) => (
                <EvidenceRow
                  key={ref.ref_id}
                  evidence={ref}
                  onOpen={() => openEvidence(ref)}
                  onUnlink={
                    canLead
                      ? () => detach.mutate(ref.ref_id, { onSuccess: () => invalidate() })
                      : undefined
                  }
                  unlinking={detach.isPending && detach.variables === ref.ref_id}
                />
              ))}
            </ul>
          )}
          {detach.error ? <ErrorMessage error={detach.error} className="mt-2" /> : null}
          <p className="mt-2.5 text-meta text-ink-500">
            Rubric: {criterion.rubricText ?? DEFAULT_RUBRIC_TEXT}
          </p>
        </DetailCard>
        <DetailCard
          title="Score over time"
          aside={
            criterion.trendChange !== null && since ? (
              <Meta>
                {formatChange(criterion.trendChange)} since {formatDayMonth(since)}
              </Meta>
            ) : undefined
          }
        >
          {trend.length > 1 ? (
            <TrendChart
              points={trend}
              thresholds={thresholds}
              label={`${criterion.name} score`}
              width={460}
              height={190}
            />
          ) : (
            <ChartEmpty title="The line starts with the first snapshot" className="py-[18px]" />
          )}
        </DetailCard>
      </div>

      {canLead ? (
        <LinkXrefModal
          key={criterion.id}
          engagementId={engagementId}
          criteria={[rubricCriterion]}
          defaultCriterionId={criterion.id}
          open={linking}
          onOpenChange={(open) => {
            setLinking(open);
            if (!open) invalidate();
          }}
        />
      ) : null}
      {scoring ? (
        <ScoreCriterionModal
          engagementId={engagementId}
          criterion={rubricCriterion}
          score={scoreItem}
          aiProposalId={candidate?.proposalId ?? null}
          open={scoring}
          onOpenChange={(next) => {
            if (!next) {
              setScoring(false);
              invalidate();
            }
          }}
        />
      ) : null}
    </div>
  );
}

/** "Contributes 3.2 of 8 points · +25 to Conditional · +40 to Go". */
function ScoreFacts({
  criterion,
  thresholds,
  points,
}: {
  criterion: HrCriterion;
  thresholds: Thresholds;
  points: string;
}) {
  const B = 'font-semibold text-ink-900';
  if (criterion.scorePercent === null) {
    return (
      <div className="flex flex-wrap gap-x-5 gap-y-2 text-body-sm text-ink-700">
        <span>
          Not scored · worth up to <b className={B}>{points}</b> points
        </span>
      </div>
    );
  }
  // One value against each gate, on the score as printed.
  const { toGo, toConditional } = gateDistance(criterion.scorePercent, thresholds);
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-2 text-body-sm text-ink-700">
      {criterion.weightedContribution !== null ? (
        <span>
          Contributes <b className={B}>{criterion.weightedContribution.toFixed(1)}</b> of {points}{' '}
          points
        </span>
      ) : null}
      {toConditional > 0 ? (
        <span>
          <b className={B}>+{toConditional}</b> to Conditional
        </span>
      ) : null}
      {toGo > 0 ? (
        <span>
          <b className={B}>+{toGo}</b> to Go
        </span>
      ) : (
        <span>At or above Go</span>
      )}
    </div>
  );
}

function EvidenceRow({
  evidence,
  onOpen,
  onUnlink,
  unlinking,
}: {
  evidence: CrossModuleReferenceItem;
  onOpen: () => void;
  onUnlink?: () => void;
  unlinking: boolean;
}) {
  const missing = evidence.resolved.status === 'missing';
  const title =
    evidence.resolved.title ??
    `${evidenceKindLabel(evidence.source_entity_kind)} ${evidence.source_entity_id.slice(0, 8)}`;
  const state = evidenceState(evidence);
  return (
    <li className="grid grid-cols-[84px_1fr_auto_auto] items-center gap-2.5 border-t border-ink-100 py-2 text-body-sm first:border-t-0">
      <span className="text-[10px] uppercase tracking-[0.05em] text-ink-500">
        {evidenceKindLabel(evidence.source_entity_kind)}
      </span>
      {missing ? (
        <span className="truncate text-ink-500">{title}</span>
      ) : (
        <button
          type="button"
          onClick={onOpen}
          aria-label={`Open ${title}`}
          className="truncate text-left text-ink-900 hover:underline hover:underline-offset-[3px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-amber-600"
        >
          {title}
        </button>
      )}
      <StatusChip tone={state.tone}>{state.text}</StatusChip>
      {onUnlink ? (
        <WorkspaceButton
          variant="tertiary"
          onClick={onUnlink}
          disabled={unlinking}
          aria-label={`Unlink ${title}`}
          className="h-6 px-1.5 text-ink-500"
        >
          {unlinking ? 'Unlinking…' : 'Unlink'}
        </WorkspaceButton>
      ) : (
        <span />
      )}
    </li>
  );
}
